import {
  Button,
  Checkbox,
  ChoiceRow,
  Field,
  FieldGrid,
  Input,
  Radio,
  RadioGroup,
  Skeleton,
} from '@westhetics/ui'
import { useRef } from 'react'
import { LoadError } from '@/app/load-error'
import { TextField } from '@/features/clinic-profile/blocks'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { useIntegrationsCatalog, type Integration, type WebhookInput } from './api'
import {
  WEBHOOK_KEYS,
  validateWebhook,
  webhookFieldKey,
  webhookInput,
  type WebhookFormState,
} from './form'

/**
 * Criar ou alterar um webhook (RN-INT-16): o endereço, a descrição e os eventos, todos ou por grupo. Os eventos e o
 * que cada um quer dizer vêm do catálogo da API; só aparecem os grupos que o app vê pelas permissões dele.
 */

const errorClass =
  'font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal'

export function WebhookForm({
  integration,
  initial,
  submitLabel,
  loadingLabel,
  loading,
  onSubmit,
  onCancel,
}: {
  integration: Integration
  initial?: { url: string; description: string | null; events: string[] }
  submitLabel: string
  loadingLabel: string
  loading: boolean
  /** Recusado pela API, os erros por campo voltam para os campos (o resto, quem chama mostra). */
  onSubmit: (input: WebhookInput) => Promise<unknown>
  onCancel: () => void
}) {
  const catalog = useIntegrationsCatalog()
  const formRef = useRef<HTMLDivElement>(null)
  const form = useFormState<WebhookFormState>(
    () => ({
      url: initial?.url ?? '',
      description: initial?.description ?? '',
      mode: !initial || initial.events.includes('*') ? 'all' : 'some',
      events: initial && !initial.events.includes('*') ? initial.events : [],
    }),
    validateWebhook,
  )
  const errors = form.errors

  function submit() {
    if (!form.check(WEBHOOK_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    onSubmit(webhookInput(form.values)).catch((error: unknown) => {
      form.refuse(placeFieldErrors<WebhookFormState>(error, webhookFieldKey).fields)
      focusFirstInvalid(formRef.current)
    })
  }

  const visibleGroups = catalog.data
    ? catalog.data.eventGroups.filter((group) =>
        integration.scopes.some(
          (scope) => scope === group.scope || scope === group.scope.replace(':read', ':write'),
        ),
      )
    : []

  function toggleEvent(type: string, on: boolean) {
    const others = form.values.events.filter((e) => e !== type)
    form.update({ events: on ? [...others, type] : others })
  }

  return (
    <div ref={formRef} className="flex flex-col gap-4">
      <FieldGrid>
        <TextField
          form={form}
          name="url"
          label="Endereço"
          type="url"
          inputMode="url"
          autoCapitalize="none"
          placeholder="https://seu-sistema.com.br/webhooks/westhetics"
          hint="https na porta 443, com nome de domínio público. A Westhetics manda um POST a cada evento."
          className="col-span-full"
        />
        <Field label="Descrição" optional error={errors.description} className="col-span-full">
          <Input
            maxLength={200}
            value={form.values.description}
            placeholder="Produção, homologação, n8n…"
            onChange={(event) => form.update({ description: event.target.value })}
            onBlur={() => form.touch('description')}
          />
        </Field>
      </FieldGrid>

      <fieldset className="flex flex-col gap-3">
        <legend className="eyebrow mb-2">Eventos</legend>
        <RadioGroup
          aria-label="Eventos"
          className="tablet:grid-cols-2"
          value={form.values.mode}
          onValueChange={(mode) => form.update({ mode: mode as WebhookFormState['mode'] })}
        >
          <ChoiceRow
            control={<Radio value="all" />}
            description="Inclusive os que entrarem depois, dentro das permissões do app."
          >
            Todos os eventos
          </ChoiceRow>
          <ChoiceRow control={<Radio value="some" />} description="Só os que você marcar.">
            Escolher os eventos
          </ChoiceRow>
        </RadioGroup>

        {form.values.mode === 'some' ? (
          catalog.isError ? (
            <LoadError
              title="Não conseguimos carregar os eventos"
              error={catalog.error}
              onRetry={() => void catalog.refetch()}
            />
          ) : catalog.isPending ? (
            <div role="status" aria-label="Carregando os eventos">
              <Skeleton className="h-[48px] w-full" />
            </div>
          ) : visibleGroups.length === 0 ? (
            <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
              O app não tem permissão de leitura em nenhum recurso, então não recebe evento. Dê a
              permissão na aba Permissões.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {visibleGroups.map((group) => (
                <div key={group.resource}>
                  <div className="font-display text-name text-ink mb-2 leading-[1.4] font-normal">
                    {group.label}
                  </div>
                  <div className="border-hairline-strong bg-surface border">
                    {catalog.data.events
                      .filter((e) => e.resource === group.resource)
                      .map((e) => (
                        <ChoiceRow
                          key={e.type}
                          bare
                          control={
                            <Checkbox
                              aria-label={e.type}
                              checked={form.values.events.includes(e.type)}
                              onCheckedChange={(on) => toggleEvent(e.type, on === true)}
                            />
                          }
                          description={e.description}
                        >
                          <code className="font-body text-ink text-[13px] font-normal">
                            {e.type}
                          </code>
                        </ChoiceRow>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : null}
        {errors.events ? (
          <p role="alert" className={errorClass}>
            {errors.events}
          </p>
        ) : null}
      </fieldset>

      <div className="flex flex-wrap items-center gap-4">
        <Button onClick={() => submit()} loading={loading} loadingLabel={loadingLabel}>
          {submitLabel}
        </Button>
        <Button variant="text" onClick={onCancel}>
          Desistir
        </Button>
      </div>
    </div>
  )
}
